/**
 * Validation schemas and utility functions for widget settings.
 * Separated from the component to enable testing without React dependencies.
 */

import { isEqual, isNil } from 'lodash';
import * as yup from 'yup';
import { CHART_TYPE } from '../../constants/enums';

/**
 * Label format type used in various widget configurations.
 */
export interface LabelFormatType {
  text: string;
  value: string;
}

/** Widget types that require Y-axis scale configuration */
export const WIDGET_TYPES_WITH_Y_SCALE: ReadonlySet<string> = new Set([
  CHART_TYPE.ChartColumn,
  CHART_TYPE.ChartKpi,
  CHART_TYPE.ComparedFrLine,
]);

/**
 * Yup transform that converts empty strings to null for optional numeric inputs.
 *
 * In HTML forms, empty number inputs submit as empty strings rather than null/undefined.
 * This transform allows Yup's `.nullable()` to work correctly by converting those
 * empty strings to null before validation.
 *
 * @param value - The value after Yup's internal type coercion (may be NaN for empty string → number)
 * @param originalValue - The raw input value before coercion (string from form input)
 * @returns `null` if originalValue is an empty string, otherwise returns value unchanged
 */
const emptyStringToNull = (value: unknown, originalValue: unknown): unknown =>
  typeof originalValue === 'string' && originalValue === '' ? null : value;

/** Type representing possible validation schema combinations returned by getValidationSchema */
export type WidgetValidationSchema = yup.ObjectSchema<yup.AnyObject>;

/** Parameters for computing isDirty state */
export interface IsDirtyParams {
  /** Count of fields that differ from their default values (from react-hook-form's dirtyFields) */
  dirtyFieldCount: number;
  colorsSelection: Record<string, string> | undefined;
  lineColorSelection: string | undefined;
  defaultColor: string | undefined;
  secondColor: string | undefined;
  sortOrder: string[] | undefined;
  originalSortOrder: string[] | undefined;
  visibleColumns: string[] | undefined;
  originalVisibleColumns: string[] | undefined;
}

/**
 * Computes whether the form has unsaved changes.
 *
 * Uses dirtyFieldCount (from Object.keys(dirtyFields).length) rather than formState.isDirty
 * because isDirty becomes true on first change but doesn't reset when values are reverted
 * to their defaults. Using dirtyFieldCount properly tracks actual differences from defaults.
 */
export function computeIsDirty({
  dirtyFieldCount,
  colorsSelection,
  lineColorSelection,
  defaultColor,
  secondColor,
  sortOrder,
  originalSortOrder,
  visibleColumns,
  originalVisibleColumns,
}: IsDirtyParams): boolean {
  // Color state starts as undefined; any defined value (including {}) indicates user interaction
  const colorsDirty =
    !isNil(colorsSelection) || !isNil(lineColorSelection) || !isNil(defaultColor) || !isNil(secondColor);
  const sortDirty = !isEqual(sortOrder, originalSortOrder);
  const columnsDirty = !isEqual(visibleColumns, originalVisibleColumns);

  return dirtyFieldCount > 0 || colorsDirty || sortDirty || columnsDirty;
}

export function isSaveDisabled(saveButtonEnabled: boolean, isDirty: boolean): boolean {
  return !saveButtonEnabled || !isDirty;
}

/**
 * Common validation schema shared by all widget types.
 *
 * **Nullable Strategy:**
 * All fields use `.nullable()` because widget configurations are partial by design.
 * Different widget types use different subsets of these fields, and the backend
 * returns `null` for unconfigured options. No fields are marked `.required()` here
 * because validation requirements vary by widget type - the schema validates data
 * shape rather than enforcing presence. Required field validation happens at the
 * widget-type level or via conditional logic in the component.
 *
 * **Note on undefined vs null:**
 * Yup's `.nullable()` accepts both `null` and `undefined`. While the backend may
 * return `null` for unconfigured options, the form's internal state uses `undefined`
 * for unset values (consistent with react-hook-form's default behavior). The schema
 * accepts both to handle data from either source without coercion.
 *
 * Field documentation:
 *
 * **Display Settings:**
 * - `displayLegend` - Whether to show the chart legend (applies to most chart types)
 * - `labelFormat` - How data labels are displayed (e.g., 'value', 'percent', 'both')
 * - `labelPlacement` - Position of labels on the chart (e.g., 'inside', 'outside')
 * - `labelDecimals` - Number of decimal places for label values
 * - `displayDecimalPlaces` - Decimal places for rolling line display values
 * - `showTop` - Limits displayed categories (e.g., 'all', 'top5', 'top10')
 * - `hideBlankCategory` - Excludes records with no value selected from the chart
 *
 * **KPI & Target Settings (CHART_COLUMN, CHART_KPI):**
 * - `kpiTargetConfiguration` - KPI target line settings including monthly values, caption, line color, and visibility
 * - `kpiTargetForColumnKpiPercentage` - Percentage-based KPI targets for column/KPI charts
 * - `secondColorEnabled` - Enables a second color for bars that meet/exceed threshold values
 *
 * **Color & Sorting:**
 * - `colors` - Custom color mapping for chart categories (Record<categoryName, hexColor>)
 * - `sortOrder` - Custom ordering of chart categories
 *
 * **Data Configuration:**
 * - `form` - Selected form(s) for data source
 * - `field` - Primary field for chart data
 * - `secondField` - Secondary field (e.g., for stacked charts or line overlays)
 * - `module` - MYOSH module containing the data
 *
 * **Filtering:**
 * - `globalFilters` - Pre-configured filter settings for the widget
 * - `notBlank` - When true, excludes records with blank values in filtered fields
 */

export type LabelFormatValue = 'number' | 'percentage' | 'both';

export const commonSchema = yup.object().shape({
  // Display settings
  displayLegend: yup.boolean().nullable(),
  labelFormat: yup.mixed<LabelFormatValue | { value: LabelFormatValue; text: string }>().nullable().default(undefined),
  labelPlacement: yup.mixed().nullable(), // Object from dropdown: { value: string, text: string }
  labelDecimals: yup.mixed<number | { value: string; text: string }>().nullable().default(undefined),
  displayDecimalPlaces: yup.mixed<number | { value: string; text: string }>().nullable().default(undefined),
  showTop: yup.mixed().nullable(), // Object from dropdown: { value: string, text: string }
  hideBlankCategory: yup.mixed().nullable(), // Object from checkbox: { checked: boolean }

  // KPI & target settings
  kpiTargetConfiguration: yup.object().nullable(),
  kpiTargetForColumnKpiPercentage: yup.object().nullable(),
  secondColorEnabled: yup.boolean().nullable(),

  // Color & sorting
  colors: yup.object().nullable(),
  sortOrder: yup.array().nullable(),

  // Data configuration
  form: yup.array().nullable(),
  field: yup.string().nullable(),
  secondField: yup.string().nullable(),
  module: yup.string().nullable(),

  // Filtering
  globalFilters: yup.object().nullable(),
  notBlank: yup.boolean().nullable(),
});

/**
 * Y-axis scale validation schema.
 * Validates that yScaleMax > yScaleMin when both are provided and are valid integers.
 */
export const yScaleSchema = yup.object().shape({
  yScaleMin: yup.number().nullable().integer('Y Scale Min must be an integer').transform(emptyStringToNull),
  yScaleMax: yup
    .number()
    .nullable()
    .integer('Y Scale Max must be an integer')
    .transform(emptyStringToNull)
    .test('max-greater-than-min', 'Max value must be greater than Min value', function (value) {
      const { yScaleMin } = this.parent;
      // Only compare when both values are present (Yup's .integer() already validated them)
      if (!isNil(value) && !isNil(yScaleMin)) {
        return value > yScaleMin;
      }
      return true;
    }),
});

/**
 * Creates a yup test function that validates field values contain all grouping criteria values.
 * Used for AI configuration fields that must include all selected grouping criteria.
 *
 * The validator ensures that every value from the grouping criteria field is also present
 * in the validated field. This enforces that users cannot remove grouping criteria from
 * the field selection without first removing them from the grouping criteria.
 *
 * @param groupingCriteriaFieldName - The name of the grouping criteria field in the parent object
 *                                    (e.g., 'injuryFrequencyGroupingCriteria' or 'hoursWorkedGroupingCriteria')
 * @returns A yup test function that returns:
 *          - `true` if all grouping criteria values exist in the field values (valid)
 *          - `false` if any grouping criteria value is missing from field values (invalid)
 *
 * @example
 * // If groupingCriteria = [{value: 'A'}, {value: 'B'}]
 * // and fieldValues = [{value: 'A'}, {value: 'B'}, {value: 'C'}]
 * // Returns true (all criteria 'A' and 'B' are present in fields)
 *
 * @example
 * // If groupingCriteria = [{value: 'A'}, {value: 'B'}]
 * // and fieldValues = [{value: 'A'}, {value: 'C'}]
 * // Returns false (criteria 'B' is missing from fields)
 */
function createGroupingCriteriaValidator(groupingCriteriaFieldName: string) {
  return function (this: yup.TestContext, value: LabelFormatType[] | undefined): boolean {
    const groupingCriteria = this.parent?.[groupingCriteriaFieldName] || [];
    const groupValues = groupingCriteria.map((item: LabelFormatType) => item.value);
    const fieldValueSet = new Set(value?.map((item: LabelFormatType) => item.value) || []);

    return groupValues.every((groupValue: string) => fieldValueSet.has(groupValue));
  };
}

/**
 * AI extension validation schema for injury frequency and hours worked configurations.
 */
export const aiExtensionSchema = yup.object().shape({
  injuryFrequencyGroupingCriteria: yup.array().nullable(),
  injuryFrequencyFields: yup
    .array()
    .min(1, 'Injury Frequency Fields is required')
    .test(
      'contains-grouping-criteria',
      'Fields must include all grouping criteria values',
      createGroupingCriteriaValidator('injuryFrequencyGroupingCriteria')
    ),
  injuryFrequencyInjuryClassificationFieldName: yup
    .object()
    .nullable()
    .required('Injury Frequency Injury Classification Field Name is required'),
  injuryFrequencyInjuryClassificationValues: yup
    .array()
    .nullable()
    .min(1, 'Injury Frequency Injury Classification Values is required'),
  hoursWorkedGroupingCriteria: yup.array().nullable(),
  hoursWorkedFields: yup
    .array()
    .min(1, 'Hours Worked Fields is required')
    .test(
      'contains-grouping-criteria',
      'Fields must include all grouping criteria values',
      createGroupingCriteriaValidator('hoursWorkedGroupingCriteria')
    ),
  hoursWorkedFieldName: yup.object().nullable().required('Hours Worked Field Name is required'),
  hoursWorkedInjuryClassificationFieldName: yup.object().nullable(),
});

/**
 * Gets the appropriate validation schema based on widget type and AI configuration state.
 *
 * @param widgetType - The type of widget (e.g., 'CHART_COLUMN', 'CHART_KPI')
 * @param showAIConfig - Whether AI configuration is enabled/visible
 * @returns The appropriate combined yup schema
 */
export function getValidationSchema(widgetType: string, showAIConfig: boolean): WidgetValidationSchema {
  const needsYScale = WIDGET_TYPES_WITH_Y_SCALE.has(widgetType);

  if (needsYScale && showAIConfig) {
    return commonSchema.concat(yScaleSchema).concat(aiExtensionSchema);
  }
  if (needsYScale) {
    return commonSchema.concat(yScaleSchema);
  }
  if (showAIConfig) {
    return commonSchema.concat(aiExtensionSchema);
  }
  return commonSchema;
}
